/**
 * 无标签数据位定位：基于样式特征为空白格生成 data 候选标记。
 *
 * 定位：extract.ts 现有逻辑（isLabelText + markData）仅覆盖标签相邻格。
 * 本模块增强定位，通过样式特征（边框/背景/数字格式/对齐）识别无标签数据位。
 *
 * 样式特征信号：
 *   - border：四边框（完整边框 → 强信号）
 *   - bgcolor：填充背景色（区别于纯白 → 中等信号）
 *   - numFmtId：数字/日期格式（→ 强信号）
 *   - alignment：右对齐/中心对齐（数字格式字段通常右对齐 → 中等信号）
 *
 * 候选生成策略：
 *   1. 扫描所有空白格（无文本、无合并主格）
 *   2. 检查样式特征强度：≥2 个中等信号 或 ≥1 个强信号 → 标记为 data 候选
 *   3. 排除特殊位置：标题行、孤立格、过大空白区 → 避免误标
 */

import type { SkeletonCell } from './extract.js';
import type { TemplateBlock, Zone, Side } from './regions.js';

/**
 * 样式特征评分（0..1）
 */
interface StyleSignals {
  hasBorder: number; // 0/0.5/1
  hasBgColor: number; // 0/0.3/1
  hasNumFmt: number; // 0/1
  isRightAligned: number; // 0/0.3
  isCenterAligned: number; // 0/0.2
}

/**
 * 数据位候选（供 markData 消费）
 */
export interface DataCellCandidate {
  row: number;
  col: number;
  reason: string;
  confidence: number; // 0..1，候选强度
}

/**
 * 从 SkeletonCell.style 提取边框信息（四边）
 * 返回 0-4（有多少条边有样式）
 */
function countBorders(style: Record<string, unknown> | undefined): number {
  if (!style) return 0;
  let count = 0;
  const borders = style.border as Record<string, unknown> | undefined;
  if (!borders) return 0;
  if (borders.left) count++;
  if (borders.right) count++;
  if (borders.top) count++;
  if (borders.bottom) count++;
  return count;
}

/**
 * 检查背景颜色是否非白色/非透明
 */
function hasSignificantBgColor(style: Record<string, unknown> | undefined): boolean {
  if (!style) return false;
  const bgcolor = style.bgcolor as string | undefined;
  if (!bgcolor) return false;
  const hex = bgcolor.toLowerCase();
  // 排除白色、浅灰色、全透明
  return !['#ffffff', '#f2f2f2', '#f9f9f9', '#dbe5f1', '#dae5f0'].includes(hex);
}

/**
 * 检查数字/日期格式特征
 */
function hasNumericFormat(style: Record<string, unknown> | undefined): boolean {
  if (!style) return false;
  const fmt = style.format as string | undefined;
  if (!fmt) return false;
  // 数字格式特征：含 0、# 或日期格式 yyyy/mm/dd
  return /[0#]|yyyy|mm|dd/i.test(fmt);
}

/**
 * 检查右对齐
 */
function isRightAligned(style: Record<string, unknown> | undefined): boolean {
  if (!style) return false;
  return style.align === 'right';
}

/**
 * 检查中心对齐
 */
function isCenterAligned(style: Record<string, unknown> | undefined): boolean {
  if (!style) return false;
  return style.align === 'center';
}

/**
 * 计算样式特征信号强度
 */
function computeStyleSignals(style: Record<string, unknown> | undefined): StyleSignals {
  const borderCount = countBorders(style);
  return {
    hasBorder: borderCount >= 3 ? 1.0 : borderCount >= 2 ? 0.5 : 0,
    hasBgColor: hasSignificantBgColor(style) ? 1.0 : 0,
    hasNumFmt: hasNumericFormat(style) ? 1.0 : 0,
    isRightAligned: isRightAligned(style) ? 0.3 : 0,
    isCenterAligned: isCenterAligned(style) ? 0.2 : 0,
  };
}

/**
 * 计算数据位候选的综合置信度
 * 权重：numFmt 30% + border 25% + bgColor 20% + align 15% + 位置信号 10%
 */
function computeConfidence(
  signals: StyleSignals,
  isInDataRegion: boolean,
  isNearLabel: boolean,
): number {
  let score = 0;
  score += signals.hasNumFmt * 0.3;
  score += signals.hasBorder * 0.25;
  score += signals.hasBgColor * 0.2;
  score += (signals.isRightAligned + signals.isCenterAligned) * 0.15;

  // 位置信号
  let posSignal = 0;
  if (isInDataRegion) posSignal += 0.06;
  if (isNearLabel) posSignal += 0.04;
  score += posSignal;

  return Math.min(score, 1.0);
}

/**
 * 检查格是否在数据区内（按 dataRegion 判断）
 */
function isInDataRegion(
  row: number,
  col: number,
  dataRegions: Array<{ headerRow: number; headerCol: number; rowCount: number }>,
): boolean {
  for (const dr of dataRegions) {
    const colRange = dr.headerCol; // 假设从表头列开始的宽度（简化，实际需要 colspan）
    if (
      row > dr.headerRow &&
      row <= dr.headerRow + dr.rowCount &&
      col >= colRange
    ) {
      return true;
    }
  }
  return false;
}

/**
 * 检查格是否接近标签（标签前/后）
 */
function isNearLabel(
  row: number,
  col: number,
  labels: Array<{ row: number; col: number }>,
  maxRow: number,
  maxCol: number,
): boolean {
  for (const label of labels) {
    const rowDist = Math.abs(row - label.row);
    const colDist = Math.abs(col - label.col);
    // 邻近定义：同行且距离 ≤1，或同列且距离 ≤1
    if ((rowDist === 0 && colDist <= 2) || (colDist === 0 && rowDist <= 2)) {
      return true;
    }
  }
  return false;
}

/**
 * 检查格是否在表头行（通常不是数据位）
 */
function isHeaderRow(
  row: number,
  headerRows: Set<number>,
): boolean {
  return headerRows.has(row);
}

/**
 * 主函数：检测无标签数据位候选
 *
 * @param cells - 已有的 SkeletonCell 列表（可能包含 label/data 标记）
 * @param textGrid - 完整文本网格
 * @param merges - 合并信息
 * @param dataRegions - 数据区列表
 * @param blocks - 区块列表（可选，用于区块内候选识别）
 * @param headerRows - 表头行集合
 * @param maxRow - 最大行数
 * @param maxCol - 最大列数
 *
 * @returns 新增的 DataCellCandidate 列表（不修改原 cells）
 */
export function detectDataSlots(options: {
  cells: SkeletonCell[];
  textGrid: string[][];
  merges: Array<{ row: number; col: number; rowspan: number; colspan: number }>;
  dataRegions: Array<{ headerRow: number; headerCol: number; rowCount: number }>;
  blocks?: TemplateBlock[];
  headerRows: Set<number>;
  maxRow: number;
  maxCol: number;
}): DataCellCandidate[] {
  const {
    cells,
    textGrid,
    merges,
    dataRegions,
    headerRows,
    maxRow,
    maxCol,
  } = options;

  // 构建已有格的索引与标签列表
  const existingCells = new Set<string>();
  const labels: Array<{ row: number; col: number }> = [];

  for (const cell of cells) {
    existingCells.add(`${cell.row},${cell.col}`);
    if (cell.candidate === 'label') {
      labels.push({ row: cell.row, col: cell.col });
    }
  }

  // 构建合并覆盖集
  const covered = new Set<string>();
  for (const m of merges) {
    for (let r = m.row; r < m.row + m.rowspan; r++) {
      for (let c = m.col; c < m.col + m.colspan; c++) {
        if (r !== m.row || c !== m.col) {
          covered.add(`${r},${c}`);
        }
      }
    }
  }

  const candidates: DataCellCandidate[] = [];

  // 扫描所有空白格
  for (let r = 1; r <= maxRow; r++) {
    // 跳过表头行
    if (isHeaderRow(r, headerRows)) continue;

    for (let c = 1; c <= maxCol; c++) {
      const key = `${r},${c}`;

      // 跳过：已有记录的格、被合并覆盖的格、有文本的格
      if (existingCells.has(key) || covered.has(key)) continue;
      if (textGrid[r]?.[c]) continue;

      // 检查样式特征
      const cellInList = cells.find((cell) => cell.row === r && cell.col === c);
      const style = cellInList?.style;
      const signals = computeStyleSignals(style);

      // 计算置信度
      const inDataRegion = isInDataRegion(r, c, dataRegions);
      const nearLabel = isNearLabel(r, c, labels, maxRow, maxCol);
      const confidence = computeConfidence(signals, inDataRegion, nearLabel);

      // 阈值：置信度 ≥ 0.3 才作为候选
      if (confidence >= 0.3) {
        const reasonParts: string[] = [];
        if (signals.hasNumFmt) reasonParts.push(`数字格式`);
        if (signals.hasBorder >= 0.5) reasonParts.push(`边框`);
        if (signals.hasBgColor) reasonParts.push(`背景色`);
        if (inDataRegion) reasonParts.push(`数据区内`);
        if (nearLabel) reasonParts.push(`标签邻近`);

        candidates.push({
          row: r,
          col: c,
          reason: `无标签数据位候选（${reasonParts.join(' + ')}）`,
          confidence,
        });
      }
    }
  }

  return candidates;
}
